/* The member record: ten tabs over one person.
 *
 * THE ORDER IS THE ORDER SOMEBODY ASKS. Overview is who they are; Activity timeline
 * is the second question anybody asks of a record, "what has actually happened with
 * this person"; and every tab after it is one slice of that same history. Nothing
 * here is alphabetical or accidental.
 */
import { useState } from 'react';
import { useAsync, useSeesCommercials, useSource } from '../data/SourceProvider.js';
import { NoneRow } from '../components/Panel.js';
import { RecordHeader } from './record/Header.js';
import { MemberEdit } from './record/MemberEdit.js';
import { Overview } from './record/Overview.js';
import { Timeline } from './record/Timeline.js';
import {
  Afp, Attendance, Details, Marketing, Sales, Scans,
} from './record/Tabs.js';
import { Macros } from './record/Macros.js';
import { Conversation } from './record/Conversation.js';
import { AddNote } from './record/AddNote.js';
import { go } from '../routes.js';
import { canResolve, cancellationOpen } from '@acrostic/model';
import { RecordCancellation, ResolveCancellation } from './record/Cancellation.js';

/* THE TEN TABS (PRD 6), and the tenth one changed on 2026-09-18.
 *
 * NOTES IS NOT A TAB, and the PRD is explicit: "A note is an event, it lands on the
 * timeline with its author and its time, and the timeline filters down to notes
 * alone for anyone who wants only those. A second store for the same sentence is
 * how two versions of it come to exist."
 *
 * CONVERSATION TAKES ITS PLACE. It is the one tab that was missing entirely.
 *
 * Adding a note did NOT move onto the timeline tab with the notes. It became a
 * persistent action beside Edit, available from whichever tab is open, "because the
 * moment somebody learns that his daughter has started at the club is not a moment
 * they will navigate for". */
const TABS = [
  ['overview', 'Overview'],
  ['timeline', 'Activity Timeline'],
  ['attendance', 'Attendance'],
  ['scans', 'InBody Scans'],
  ['macros', 'Macros'],
  ['afp', 'Fitness Plan'],
  ['sales', 'Memberships & Sales'],
  ['marketing', 'Marketing'],
  ['conversation', 'Conversation'],
  ['details', 'Details'],
] as const;

type TabKey = (typeof TABS)[number][0];

/* THE WHOLE TAB GOES, not its contents. Six blank rows under a heading called
   Memberships & Sales reads as an import that failed; no tab reads as a seat that
   does not deal with money, which is what is true. */
const COMMERCIAL_TABS = new Set<TabKey>(['sales']);

export function MemberRecord({ id }: { id: string }) {
  const source = useSource();
  const money = useSeesCommercials();
  const [tab, setTab] = useState<TabKey>('overview');
  const tabs = TABS.filter(([k]) => money || !COMMERCIAL_TABS.has(k));
  /* Anchors are written from this screen now, so it has to be able to re-read
     itself. Bumping the epoch is how every other screen here reloads. */
  const [epoch, setEpoch] = useState(0);
  const [editing, setEditing] = useState(false);
  const [asking, setAsking] = useState(false);
  const [noting, setNoting] = useState(false);
  const [resolving, setResolving] = useState(false);
  const { data: m, error } = useAsync(() => source.getMember(id), [source, id, epoch]);
  /* THE TRACK STANDS WHERE THE SCORE WOULD BE (PRD 20). Null for everybody past day
     90, which is the common case, so the header asks for it without checking first.
     `epoch` is in the deps because recording a touch is what moves it. */
  const { data: first90 } = useAsync(() => source.getFirstNinety(id), [source, id, epoch]);
  /* Who is looking, for the one action on this screen that is the Owner's alone. */
  const { data: session } = useAsync(() => source.getSession(), [source]);
  const roles = session?.user.roles;
  const ownerHere = canResolve(roles);

  if (error) return <NoneRow>Could not read the record. {error.message}</NoneRow>;
  if (!m) return <NoneRow>Reading the record…</NoneRow>;

  /* visits, scans and notes ride on the record rather than being fetched per tab:
     one read of a person, not four. getMember returns MemberRecord for exactly this,
     so none of them needs a cast.
 
     UNDEFINED IS NOT EMPTY. A source that does not send a member's history sends
     nothing, and a source that sends a member with no visits sends []. Defaulting
     the first to the second would put "No visits on record" on a gym with nine
     thousand of them, which is a lie the screen has no way to know it is telling.
     The tabs are handed the raw field and say which it is. */
  const { visits, scans } = m;
  /* Open only: a resolved one is history and lives on the timeline. */
  const cancelling = cancellationOpen(m);

  return (
    <>
      <div className="recordbar">
        <button type="button" className="linkish t-label-small" onClick={() => go('/members')}>
          ← All members
        </button>
        <span className="spacer" />
        {/* EVERY SEAT MAY OPEN IT. What they find inside is the permission — a
            coach gets name, contact and why; the money fields are simply not
            there, and a status change goes for sign-off. Hiding the button
            instead would leave a coach unable to fix a phone number. */}
        {/* CANCELLATION INITIATED (PRD 4), and the asymmetry is the rule. Recording
            one is open to everybody, because it is heard rather than decided and
            whoever was on the desk is the commonest person to hear it. Closing one
            reads "Owner closes this" for everyone else rather than disappearing: a
            control that is simply absent teaches nobody that the Owner speaks. */}
        {cancelling ? (
          <button type="button" className="btn t-label-small" onClick={() => setResolving(true)}>
            {ownerHere ? 'Close the cancellation' : 'Owner closes this'}
          </button>
        ) : (
          <button type="button" className="btn t-label-small" onClick={() => setAsking(true)}>
            Asked about cancelling
          </button>
        )}
        {/* A PERSISTENT ACTION, not a tab (PRD 6). It sits with the header actions
            and works from whichever tab is open, because the moment somebody learns
            that his daughter has started at the club is not a moment they will
            navigate for. */}
        <button type="button" className="btn t-label-small" onClick={() => setNoting(true)}>
          Add note
        </button>
        <button type="button" className="btn t-label-small" onClick={() => setEditing(true)}>
          Edit
        </button>
      </div>

      <RecordHeader m={m} first90={first90 ?? null} />

      <div className="tabstrip" role="tablist">
        {tabs.map(([key, label]) => (
          <button
            key={key} type="button" role="tab"
            aria-selected={tab === key}
            className={'tab t-label-medium' + (tab === key ? ' on' : '')}
            onClick={() => setTab(key)}
          >{label}</button>
        ))}
      </div>

      {tab === 'overview' && <Overview m={m} onChanged={() => setEpoch((n) => n + 1)} />}
      {tab === 'timeline' && <Timeline id={m.id} />}
      {tab === 'attendance' && <Attendance m={m} visits={visits} total={m.historyTotals?.visits} />}
      {tab === 'scans' && <Scans scans={scans} total={m.historyTotals?.scans} />}
      {tab === 'macros' && <Macros m={m} scans={scans} onChanged={() => setEpoch((n) => n + 1)} />}
      {tab === 'afp' && <Afp m={m} />}
      {tab === 'sales' && money && <Sales m={m} />}
      {tab === 'marketing' && <Marketing m={m} />}
      {tab === 'details' && <Details m={m} />}
      {tab === 'conversation' && <Conversation m={m} />}

      {editing && (
        <MemberEdit
          m={m} canSeeCommercials={money}
          onClose={() => setEditing(false)}
          onDone={() => { setEditing(false); setEpoch((n) => n + 1); }}
        />
      )}

      {noting && (
        <AddNote
          m={m}
          onClose={() => setNoting(false)}
          onDone={() => { setNoting(false); setEpoch((n) => n + 1); }}
        />
      )}

      {asking && (
        <RecordCancellation
          m={m}
          onClose={() => setAsking(false)}
          onDone={() => { setAsking(false); setEpoch((n) => n + 1); }}
        />
      )}

      {resolving && (
        <ResolveCancellation
          m={m} by={session?.user.name ?? ''} roles={roles}
          onClose={() => setResolving(false)}
          onDone={() => { setResolving(false); setEpoch((n) => n + 1); }}
        />
      )}
    </>
  );
}
